// Types + petites aides partagés par la sous-surface « Analyse approfondie » (DeepAnalyzePanel)
// et la carte « Médias orphelins ». Les DTO reflètent les réponses camelCase de l'API admin
// (/api/checks/catalog, /api/checks/history, /api/storage/orphans/scan) : le catalogue reste
// code-first côté C# (BccCheckCatalog), rien à maintenir en double ici.

/** Une entrée du catalogue code-first des contrôles (GET /api/checks/catalog). */
export type CheckDescriptorDto = {
	id: string;
	label: string;
	scopes: string[];
	runEndpoint: string;
	/** Absent/nul quand le contrôle n'a pas de correction automatique. */
	fixEndpoint?: string | null;
};

/** Une exécution historisée d'un contrôle (GET /api/checks/history, tri décroissant). */
export type CheckRunDto = {
	checkId: string;
	scope: string;
	errorCount: number;
	warningCount: number;
	/** Compteur informatif optionnel (ex. total d'éléments analysés). */
	infoCount?: number | null;
	durationMs: number;
	startedAtUtc: string;
	actor?: string | null;
};

export type MediaOrphanUsageDto = { contentId: string; schemaKey: string };

/** Verdict par fichier du scan de médias orphelins. */
export type MediaOrphanFileDto = {
	id?: string;
	key: string;
	folder: string;
	size: number;
	/** « used » | « orphan ». */
	verdict: string;
	usedBy?: MediaOrphanUsageDto[];
};

/** Une ligne PdfExtractedTexts dont le média n'existe plus (la fuite connue du delete). */
export type PdfExtractedTextLeakDto = {
	storageKey: string;
	wordCount: number;
	extractedAt: string;
};

/** Rapport agrégé du scan de médias orphelins : purement informatif, rien n'est supprimé. */
export type MediaOrphanReportDto = {
	totalFiles: number;
	usedCount: number;
	orphanCount: number;
	orphanBytes: number;
	files: MediaOrphanFileDto[];
	pdfExtractedTextLeaks: PdfExtractedTextLeakDto[];
	scannedAt?: string;
	elapsedMs?: number;
};

/** Erreur axios → message utilisateur : les champs « error »/« message » du serveur (déjà en FR,
 * ex. le 503 « scanner non configuré ») gagnent sur le texte générique d'axios. */
export const extractErrorMessage = (err: any, fallback: string): string =>
	String(err?.response?.data?.error ?? err?.response?.data?.message ?? err?.message ?? fallback);

/** Taille lisible FR (o, Ko, Mo, Go, To) avec virgule décimale. */
export const formatBytes = (bytes: number): string => {
	if (!Number.isFinite(bytes) || bytes <= 0) return "0 o";
	if (bytes < 1024) return `${Math.round(bytes)} o`;
	const units = ["Ko", "Mo", "Go", "To"];
	let value = bytes;
	let unit = -1;
	do {
		value /= 1024;
		unit += 1;
	} while (value >= 1024 && unit < units.length - 1);
	const text = value >= 100 ? String(Math.round(value)) : value.toFixed(1).replace(".", ",");
	return `${text} ${units[unit]}`;
};

/** Date + heure courtes FR ; « : » si absente ou invalide. */
export const formatDateTimeFr = (iso?: string | null): string => {
	if (!iso) return "-";
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return "-";
	return date.toLocaleString("fr-FR", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit"
	});
};
